import {FC, useEffect, useState} from 'react'
import { KTSVG, WithChildren } from '../../../_metronic/helpers';
import { format } from 'date-fns';
import { isPopulatedArray, parseTimestamp, showDate } from '../helpers';
import { DateSelector } from './DateSelector';
import { FileUploader, FileUploaderResponse } from './FileUploader';
import { ApplicationAttachment } from '../../modules/core/applications';
import { ApplicationFileDownload } from './ApplicationFileDownload';
import clsx from 'clsx';
import { AttachmentCard } from '../../modules/core/attachments/AttachmentCard';
import { cloneDeep } from 'lodash';
import { FormSwitch } from './CustomFormFields';


type ActionProps = {
  show: boolean
  inputType: string
  label: string
  doSave: Function
  doCancel: Function
  toggleShow: Function
}

const EditActionButtons = ({ show, inputType, label, doSave, doCancel, toggleShow} : ActionProps) => {

  if (! show) {
    return (
      <button type="button" className="btn btn-icon btn-sm p-0 h-15px w-15px text-hover-primary" onClick={() => toggleShow()} title={`Edit ${label}`}>
        <KTSVG path='/media/icons/duotune/general/gen055.svg' className='svg-icon-0 m-0' />
      </button>
    )
  }

  return (
  <div className={clsx(
    'd-flex',
    {'w-30px flex-column': ['textarea','fileUpload'].includes(inputType)},
    {'w-60px flex-row justify-content-end': !(['textarea','fileUpload'].includes(inputType)) }
  )}>
    <button type="button" className="btn btn-icon btn-sm p-0 h-25px w-25px text-hover-success me-2px" onClick={() => doSave()} title={`Save`}>
      <KTSVG path='/media/icons/duotune/general/gen037.svg' className='svg-icon-1 m-0' />
    </button>
    <button type="button" className="btn btn-icon btn-sm p-0 h-25px w-25px text-hover-danger" onClick={() => doCancel()} title={`Cancel`}>
      <KTSVG path='/media/icons/duotune/general/gen034.svg' className='svg-icon-1 m-0' />
    </button>
  </div>
  )
}

type Props = {
  containerClass: string
  name: string
  label: string
  fieldClass?: string
  value?: string
  onEdit?: Function
  clearOnSubmit?: boolean
}

const EditableInput: FC<Props & WithChildren> =  ({children, containerClass, fieldClass = '', name, label, value = '', onEdit}) => {

  const [show_editor, toggleEditor] = useState(false);
  const [current_value, setCurrentValue] = useState('');

  const doSave = () => {
    if (onEdit && current_value !== value) {
      // save input/textarea
      onEdit({ name, value: current_value});
    }
    toggleEditor(false);
  }

  useEffect(() => {
    if (value === undefined) return;
    setCurrentValue(value);
  }, [value])


  return (
  <div className={`d-flex flex-row justify-content-start align-items-start gap-2 ${containerClass}`}>
  { show_editor ? (
    <input
      type="text"
      name={name}
      value={current_value}
      className='flex-grow-1 form-control form-control-solid form-control-sm lh-1 py-1 min-h-15px'
      onChange={(e) => { setCurrentValue(e.target.value)}}
      onKeyDown={(e) => {
        // abort on Escape
        if (e && e.key) {
          if (e.key === 'Escape') {
            e.preventDefault();
            toggleEditor(false);
          } else if (e.key === 'Enter') {
            e.preventDefault();
            doSave();
          }
        }
      }}
    />
  ) : (
  <div className={`flex-grow-1 ${fieldClass}`} style={{whiteSpace: 'pre-line'}}>
    {value &&
    <div className="d-inline">{value}</div>
    }
    {children}
  </div>  
  )}
  { onEdit &&
    <EditActionButtons
      show={show_editor}
      inputType="input"
      label={label}
      toggleShow={() => toggleEditor(true)}
      doCancel={() => toggleEditor(false)}
      doSave={doSave}
    />
  }
  </div>
  )
}

const EditableTextarea: FC<Props> =  ({containerClass, fieldClass = '', name, label, value = '', onEdit, clearOnSubmit}) => {

  const [show_editor, toggleEditor] = useState(false);
  const [current_value, setCurrentValue] = useState('');

  const doSave = () => {
    if (onEdit && current_value !== value) {
      // save input/textarea
      onEdit({ name, value: current_value});
    }
    if (clearOnSubmit) setCurrentValue('');
    toggleEditor(false);
  }

  useEffect(() => {
    if (value === undefined) return;
    setCurrentValue(value);
  }, [value])


  return (
  <div className={`d-flex flex-row justify-content-start align-items-start gap-2 ${containerClass}`}>
  { show_editor ? (
     <textarea
      name={name}
      value={current_value}
      spellCheck={false}
      className='flex-grow-1 form-control form-control-solid form-control-sm'
      rows={2}
      onChange={(e) => { setCurrentValue(e.target.value)}}
      onKeyDown={(e) => {
        // abort on Escape
        if (e && e.key && e.key === 'Esc') {
            e.preventDefault();
            toggleEditor(false);
        }
      }}
    />
  ) : (
  <>
    {value &&
    <div className={`flex-grow-1 ${fieldClass}`} style={{whiteSpace: 'pre-line'}}>
      {value}
    </div>
    }
  </>
  )}
  { onEdit &&
    <EditActionButtons
      show={show_editor}
      inputType="textarea"
      label={label}
      toggleShow={() => toggleEditor(true)}
      doCancel={() => toggleEditor(false)}
      doSave={doSave}
    />
  }
  </div>
  )
}


type FileProps = {
  fileData: ApplicationAttachment
  downloadable: boolean
}
const EditableFile: FC<Props & FileProps> =  ({containerClass, name, label, fileData, downloadable = false, onEdit}) => {

  const [file_name, setFileName] = useState('');
  const [file_info, setFileInfo] = useState<FileUploaderResponse | null>(null);
  const [show_editor, toggleEditor] = useState(false);

  const doSave = () => {
    if (onEdit && file_info) {
      onEdit({ name, value: file_info.id, dat: file_info});
    }
    toggleEditor(false);
  }

  useEffect(() => {
    if (! (fileData && fileData.fileName)) return;
    setFileName(fileData.fileName);
  }, [fileData])


  return (
  <div className={`flex-grow-1 d-flex flex-row justify-content-start align-items-start gap-2 ${containerClass}`}>
  { show_editor ? (
    <FileUploader
      mainTitle={label}
      subTitle={file_name}
      fileName={file_name}
      allowMultiple={false}
      acceptFiles="UserFileFormats"
      onFileUploaded={ (dat: FileUploaderResponse) => {
        setFileName(dat.name);
        setFileInfo(dat);
      }}
      onClearFile={ () => {
        setFileName('');
        setFileInfo(null);
      }}
    />
  ) : (
  <div className='flex-grow-1'>
  { (fileData && downloadable) ? (
    <ApplicationFileDownload file={fileData} />
  ) : (
    `${fileData && fileData.fileName ? fileData.fileName : ''}`
  )
  }
  </div>
  )}
  { onEdit &&
    <EditActionButtons
      show={show_editor}
      inputType="fileUpload"
      label={label}
      toggleShow={() => toggleEditor(true)}
      doCancel={() => toggleEditor(false)}
      doSave={doSave}
    />
  }
  </div>
  )
}


type MultiFileProps = {
  fileData: ApplicationAttachment | Array<ApplicationAttachment>
  downloadable: boolean
}
const EditableMultiFile: FC<Props & MultiFileProps> =  ({containerClass, name, label, fileData, downloadable = false, onEdit}) => {

  const [show_editor, toggleEditor] = useState(false);
  const [files, setFiles] = useState<Array<ApplicationAttachment>>([]);

  const doSave = () => {
    if (onEdit) {
      // return array of file ids
      onEdit({ name, value: files.map(file => (file.id))});
    }
    toggleEditor(false);
  }

  useEffect(() => {
    if (! (fileData)) {
      setFiles([]);
      return;
    }

    if (Array.isArray(fileData)) {
      setFiles(fileData);
    } else if (typeof fileData === 'object' && fileData?.id && fileData?.fileName) {
      setFiles([fileData]);
    }

  }, [fileData])

  return (
  <div className={`d-flex flex-row justify-content-start align-items-start gap-2 ${containerClass}`}>
  { show_editor ? (
    <div className='d-flex flex-column'>
      <FileUploader
        mainTitle={label}
        subTitle={"Click or drop here to upload."}
        fileName=""
        allowMultiple={false}
        acceptFiles="UserFileFormats"
        onFileUploaded={ (dat: FileUploaderResponse) => {
          let _files = cloneDeep(files);
          // @ts-ignore
          _files.push({ id: dat.id, fileName: dat.name, deleted: false});
          setFiles(_files);
        }}
        onClearFile={ () => {
          // doNothing
        }}
      />
      <div className='d-flex flex-row flex-wrap'>
      { (files && isPopulatedArray(files)) &&
      files.map((file, idx) => (
        <AttachmentCard
            key={`editAttachment${idx}`}
            className="border py-1"
            idx={idx}
            fileId={file.id}
            fileName={file.fileName}
            onRemove={(e: any) => {
              e.stopPropagation();
              const elem = e.target.closest('.attachment-card');
              if (elem) {
                const {idx} = elem.dataset;          
                let _files = cloneDeep(files);
                _files.splice(parseInt(`${idx}`), 1);
                setFiles(_files);
              }
            }}
        />
      ))
      }
      </div>
    </div>
  ) : (
  <div className='flex-grow-1'>
    <div className='d-flex flex-column'>
    { (files && isPopulatedArray(files)) &&
    files.map((file, idx) => (
      <div key={`multifile${idx}`}>
        { downloadable ? (
          <ApplicationFileDownload file={file} />
        ) : (
          <span>{`${file.fileName}`}</span>
        )}
      </div>
    ))
    }
    </div>
  </div>
  )}
  { onEdit &&
    <EditActionButtons
      show={show_editor}
      inputType="fileUpload"
      label={label}
      toggleShow={() => toggleEditor(true)}
      doCancel={() => toggleEditor(false)}
      doSave={doSave}
    />
  }
  </div>
  )
}




const EditableSelect: FC<Props & { selectOptions: Array<{val: string, desc: string}>, display: string, noblank?: boolean}> =  ({containerClass, fieldClass = '', name, label, value = '', selectOptions, onEdit, display = '', noblank}) => {

  const [show_editor, toggleEditor] = useState(false);
  const [current_value, setCurrentValue] = useState('');
  const [current_text, setCurrentText] = useState('');

  const doSave = () => {
    if (onEdit && current_value !== value) {
      // save input/textarea
      onEdit({ name, value: current_value, dat: current_text });
    }
    toggleEditor(false);
  }

  useEffect(() => {
    if (! (value)) return;
    setCurrentValue(value);
  }, [value])


  return (
  <div className={`d-flex flex-row justify-content-start align-items-start gap-2 ${containerClass}`}>
  { show_editor ? (
    <select
      className='form-select form-select-sm form-select-solid'
      name={name}
      value={current_value}
      onChange={(e) => {
        const sel = e.target;
        setCurrentValue(sel.value);
        setCurrentText(sel.options[sel.selectedIndex].text);
      }}
    >
      {(! noblank) &&
      <option value="" disabled></option>
      }
      { selectOptions && selectOptions.map((obj, idx) => (
      <option key={`selopt${idx}`} value={obj.val}>{obj.desc}</option>
      ))}
    </select>
  ) : (
  <>
    {value &&
    <div className={`flex-grow-1 ${fieldClass}`} style={{whiteSpace: 'pre-line'}}>
      {display}
    </div>
    }
  </>
  )}
  { onEdit &&
    <EditActionButtons
      show={show_editor}
      inputType="select"
      label={label}
      toggleShow={() => toggleEditor(true)}
      doCancel={() => toggleEditor(false)}
      doSave={doSave}
    />
  }
  </div>
  )
}

const EditableDate: FC<Props & {dateFormat?: string}> =  ({containerClass, fieldClass = '', dateFormat = 'dateOnly', name, label, value = '', onEdit}) => {

  const [current_date, setCurrentDate] = useState<Date | null>(null);
  const [show_editor, toggleEditor] = useState(false);

  const doSave = () => {
    const _dateval = current_date ? format(current_date, "yyyy-MM-dd'T'HH:mm:ssxx") : '';

    if (onEdit && _dateval !== value) {
      onEdit({ name, value: _dateval});
    }
    toggleEditor(false);
  }

  useEffect(() => {
    if (value === undefined) return;
    setCurrentDate(parseTimestamp(value));
  }, [value])


  return (
  <div className={`d-flex flex-row justify-content-start align-items-start gap-2 ${containerClass}`}>
  { show_editor ? (
    <DateSelector
      className='form-control form-control-sm form-control-solid'
      selected={current_date}
      onChange={(newdt) => { setCurrentDate(newdt)}}
      dateFormat="LL"
    />
  ) : (
  <div className={`flex-grow-1 ${fieldClass}`}>
    {value &&
    showDate(value, dateFormat)
    }
  </div>
  )}
  { onEdit &&
    <EditActionButtons
      show={show_editor}
      inputType="dateSelector"
      label={label}
      toggleShow={() => toggleEditor(true)}
      doCancel={() => toggleEditor(false)}
      doSave={doSave}
    />
  }
  </div>
  )
}



const EditableSwitch: FC<Props> =  ({containerClass, fieldClass = '', name, value, label, onEdit}) => {

  const [yesno_value, setValue] = useState<'YES'|'NO'>('NO');
  const [show_editor, toggleEditor] = useState(false);

  const doSave = () => {
    
    if (onEdit) {
      onEdit({ name, value: yesno_value === 'YES' ? true : false});
    }
    toggleEditor(false);
  }

  useEffect(() => {
    setValue(value && value === 'YES' ? 'YES' : 'NO');
  }, [value]);

  return (
  <div className={`d-flex flex-row justify-content-start align-items-start gap-2 ${containerClass}`}>
  { show_editor ? (
    <FormSwitch name="editSwitch" value={yesno_value === 'YES' ? true : false} onChange={(flg:any) => setValue(flg ? 'YES' : 'NO')} />
   ) : (
  <div className={`flex-grow-1 ${fieldClass}`}>
    { yesno_value }
  </div>
  )}
  { onEdit &&
    <EditActionButtons
      show={show_editor}
      inputType="checkbox"
      label={label}
      toggleShow={() => toggleEditor(true)}
      doCancel={() => toggleEditor(false)}
      doSave={doSave}
    />
  }
  </div>
  )
}

export {EditableInput, EditableTextarea, EditableFile, EditableMultiFile, EditableSelect, EditableDate, EditableSwitch}